import { CollectionData, PaginationResults } from "@/lib/type";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  Order,
  OrderQuery,
  OrderStatus,
  UpdateOrderRequest,
} from "./order.type";
import { OrderItemStatus } from "./order.type";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import { CreatePosOrderRequest } from "../pos/pos.type";
import { normalizeOrder } from "./normalizeOrder";

export const OrderApi = createApi({
  reducerPath: "orderApi",
  tagTypes: ["order-list", "order-detail"],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    getOrders: build.query<
      PaginationResults<Order>,
      { restaurantId: string; query?: OrderQuery }
    >({
      query: ({ restaurantId, query }) => {
        if (restaurantId) {
          return {
            url: `restaurants/${restaurantId}/orders`,
            method: "GET",
            params: query,
          };
        }
        return {
          url: "orders",
          method: "GET",
          params: query,
        };
      },
      providesTags: ["order-list"],
      transformResponse: (data: CollectionData<Order>) => {
        return {
          results: (data["hydra:member"] ?? []).map((o) => normalizeOrder(o)),
          count: data["hydra:totalItems"],
        };
      },
    }),

    changeOrderStatus: build.mutation<
      Order,
      { id: string; status: OrderStatus; waiter?: string }
    >({
      query: ({ id, status, waiter }) => ({
        url: `orders/${id}/status`,
        method: "PATCH",
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
        body: JSON.stringify({ status, waiter }),
      }),
      invalidatesTags: ["order-detail"],
      transformResponse: (data: Order) => normalizeOrder(data),
      async onQueryStarted(
        { id, status },
        { dispatch, queryFulfilled, getState },
      ) {
        const patchResults = patchOrderInListCaches(
          dispatch,
          getState,
          id,
          (order) => {
            order.status = status;
          },
        );
        try {
          const { data: updatedOrder } = await queryFulfilled;
          replaceOrderInListCaches(dispatch, getState, updatedOrder);
        } catch {
          patchResults.forEach((p) => p.undo());
        }
      },
    }),

    getOrderById: build.query<Order, { id: string }>({
      query: ({ id }) => `orders/${id}`,
      providesTags: ["order-detail"],
      transformResponse: (data: Order) => normalizeOrder(data),
    }),

    updateOrder: build.mutation<Order, { id: string; data: UpdateOrderRequest }>({
      query: ({ id, data }) => ({
        url: `orders/${id}`,
        method: "PATCH",
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
        body: JSON.stringify(data),
      }),
      invalidatesTags: ["order-list", "order-detail"],
      transformResponse: (data: Order) => normalizeOrder(data),
    }),

    mergeOrders: build.mutation<Order, { orderIds: string[] }>({
      query: ({ orderIds }) => ({
        url: "orders/merge",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orders: orderIds.map((id) => `/api/orders/${id}`),
        }),
      }),
      invalidatesTags: ["order-list", "order-detail"],
      transformResponse: (data: Order) => normalizeOrder(data),
    }),

    createPosOrder: build.mutation<Order, CreatePosOrderRequest>({
      query: (body) => ({
        url: "orders",
        method: "POST",
        body,
      }),
      invalidatesTags: ["order-list"],
      transformResponse: (data: Order) => normalizeOrder(data),
    }),

    updateOrderItemStatus: build.mutation<
      Order,
      { orderUuid: string; itemUuid: string; status: OrderItemStatus }
    >({
      query: ({ orderUuid, itemUuid, status }) => ({
        url: `orders/${orderUuid}/items/${itemUuid}/status`,
        method: "PATCH",
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
        body: JSON.stringify({ status }),
      }),
      invalidatesTags: ["order-detail"],
      transformResponse: (data: Order) => normalizeOrder(data),
      async onQueryStarted(
        { orderUuid, itemUuid, status },
        { dispatch, queryFulfilled, getState },
      ) {
        const patchResults = patchOrderInListCaches(
          dispatch,
          getState,
          orderUuid,
          (order) => {
            const item = order.orderItems.find((i) => i.id === itemUuid);
            if (item) item.itemStatus = status;
          },
        );
        try {
          const { data: updatedOrder } = await queryFulfilled;
          replaceOrderInListCaches(dispatch, getState, updatedOrder);
        } catch {
          patchResults.forEach((p) => p.undo());
        }
      },
    }),

    updateComboItemStatus: build.mutation<
      Order,
      { orderUuid: string; itemUuid: string; status: OrderItemStatus }
    >({
      query: ({ orderUuid, itemUuid, status }) => ({
        url: `orders/${orderUuid}/combo_items/${itemUuid}/status`,
        method: "PATCH",
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
        body: JSON.stringify({ status }),
      }),
      invalidatesTags: ["order-detail"],
      transformResponse: (data: Order) => normalizeOrder(data),
      async onQueryStarted(
        { orderUuid, itemUuid, status },
        { dispatch, queryFulfilled, getState },
      ) {
        const patchResults = patchOrderInListCaches(
          dispatch,
          getState,
          orderUuid,
          (order) => {
            const item = order.menuComboOrderItems?.find(
              (i) => i.id === itemUuid,
            );
            if (item) item.itemStatus = status;
          },
        );
        try {
          const { data: updatedOrder } = await queryFulfilled;
          replaceOrderInListCaches(dispatch, getState, updatedOrder);
        } catch {
          patchResults.forEach((p) => p.undo());
        }
      },
    }),

    updateFormulaItemStatus: build.mutation<
      Order,
      { orderUuid: string; itemUuid: string; status: OrderItemStatus }
    >({
      query: ({ orderUuid, itemUuid, status }) => ({
        url: `orders/${orderUuid}/formula_items/${itemUuid}/status`,
        method: "PATCH",
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
        body: JSON.stringify({ status }),
      }),
      invalidatesTags: ["order-detail"],
      transformResponse: (data: Order) => normalizeOrder(data),
      async onQueryStarted(
        { orderUuid, itemUuid, status },
        { dispatch, queryFulfilled, getState },
      ) {
        const patchResults = patchOrderInListCaches(
          dispatch,
          getState,
          orderUuid,
          (order) => {
            const item = order.menuFormulaOrderItems?.find(
              (i) => i.id === itemUuid,
            );
            if (item) item.itemStatus = status;
          },
        );
        try {
          const { data: updatedOrder } = await queryFulfilled;
          replaceOrderInListCaches(dispatch, getState, updatedOrder);
        } catch {
          patchResults.forEach((p) => p.undo());
        }
      },
    }),
  }),
});

type GetOrdersArgs = { restaurantId: string; query?: OrderQuery };

function patchOrderInListCaches(
  dispatch: any,
  getState: () => any,
  orderUuid: string,
  patcher: (order: Order) => void,
) {
  const patchResults: ReturnType<
    ReturnType<typeof OrderApi.util.updateQueryData>
  >[] = [];
  const entries = OrderApi.util.selectInvalidatedBy(getState(), [
    { type: "order-list" },
  ]);
  for (const { endpointName, originalArgs } of entries) {
    if (endpointName !== "getOrders") continue;
    patchResults.push(
      dispatch(
        OrderApi.util.updateQueryData(
          "getOrders",
          originalArgs as GetOrdersArgs,
          (draft) => {
            const order = draft.results.find((o) => o.id === orderUuid);
            if (order) patcher(order);
          },
        ),
      ),
    );
  }
  return patchResults;
}

function replaceOrderInListCaches(
  dispatch: any,
  getState: () => any,
  updatedOrder: Order,
) {
  const entries = OrderApi.util.selectInvalidatedBy(getState(), [
    { type: "order-list" },
  ]);
  for (const { endpointName, originalArgs } of entries) {
    if (endpointName !== "getOrders") continue;
    dispatch(
      OrderApi.util.updateQueryData(
        "getOrders",
        originalArgs as GetOrdersArgs,
        (draft) => {
          const idx = draft.results.findIndex(
            (o) => o.id === updatedOrder.id,
          );
          if (idx !== -1) {
            draft.results[idx] = updatedOrder;
          }
        },
      ),
    );
  }
}

export const {
  useGetOrdersQuery,
  useChangeOrderStatusMutation,
  useGetOrderByIdQuery,
  useUpdateOrderMutation,
  useMergeOrdersMutation,
  useCreatePosOrderMutation,
  useUpdateOrderItemStatusMutation,
  useUpdateComboItemStatusMutation,
  useUpdateFormulaItemStatusMutation,
} = OrderApi;
